/**
 * Cross-tab state synchronization types and utilities.
 *
 * Uses BroadcastChannel API for real-time communication between tabs
 * and localStorage for persistent lock state.
 */

// Unique tab identifier (generated once per tab session)
export const TAB_ID = `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// BroadcastChannel name
export const CHANNEL_NAME = 'antioch-shared-state';

// Storage keys
export const STREAM_LOCK_KEY = 'antioch:stream:lock';
export const CONNECTED_WORKSPACE_KEY = 'antioch:workspace:connected';

// Lock timeout configuration
export const STREAM_LOCK_TIMEOUT_MS = 15_000; // Lock is stale after 15s without heartbeat
export const STREAM_LOCK_HEARTBEAT_MS = 5_000; // Heartbeat every 5s
// Crash-recovery fallback only: the `storage` event clears locks promptly in
// the normal cross-tab case, so this interval just covers an owner tab that
// died without firing beforeunload. Aligned to the heartbeat cadence to
// minimize idle wakeups; recovery still lands within STREAM_LOCK_TIMEOUT_MS.
export const STREAM_LOCK_STALE_CHECK_MS = 5_000;

/**
 * Stream lock object stored in localStorage.
 */
export interface StreamLock {
  tabId: string;
  workspaceId: string;
  timestamp: number;
}

/**
 * Connected workspace info stored in localStorage.
 */
export interface ConnectedWorkspaceInfo {
  id: string;
  name: string;
  tabId: string;
  timestamp: number;
}

/**
 * Messages sent via BroadcastChannel between tabs.
 */
export type SharedMessage =
  | { type: 'STREAM_LOCK_ACQUIRED'; tabId: string; workspaceId: string }
  | { type: 'STREAM_LOCK_RELEASED'; tabId: string }
  | { type: 'REQUEST_STREAM_TRANSFER'; fromTabId: string; toTabId: string; workspaceId: string }
  | { type: 'STREAM_TRANSFER_ACCEPTED'; fromTabId: string }
  | { type: 'STREAM_TRANSFER_DENIED'; fromTabId: string }
  | { type: 'WORKSPACE_CONNECTED'; workspaceId: string; workspaceName: string; tabId: string }
  | { type: 'WORKSPACE_DISCONNECTED'; tabId: string }
  | { type: 'NOTEBOOK_CHANGED'; path: string; workspaceId: string; tabId: string }
  | { type: 'NOTEBOOK_CELLS_UPDATED'; path: string; workspaceId: string; tabId: string }
  | { type: 'TAB_CLOSING'; tabId: string };

/**
 * Check if a stream lock is stale (expired).
 */
export function isLockStale(lock: StreamLock): boolean {
  return Date.now() - lock.timestamp > STREAM_LOCK_TIMEOUT_MS;
}

/**
 * Safely post a message on a BroadcastChannel.
 *
 * BroadcastChannel.postMessage() throws InvalidStateError if the channel
 * has been closed. Closure can race with queued callers (heartbeat
 * intervals, message handlers, beforeunload, async state-setters), so
 * call sites need defensive handling. This helper centralizes the
 * try/catch for that specific race; other error classes (e.g.
 * DataCloneError when a non-cloneable message slips in) re-throw so
 * genuine bugs are not silently masked.
 *
 * Returns true if the message was posted, false if the channel was
 * missing or already closed.
 */
export function safePostMessage(channel: BroadcastChannel | null, message: SharedMessage): boolean {
  if (!channel) return false;
  try {
    channel.postMessage(message);
    return true;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'InvalidStateError') {
      return false;
    }
    throw err;
  }
}

/**
 * Get the current stream lock from localStorage.
 */
export function getStreamLock(): StreamLock | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(STREAM_LOCK_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as StreamLock;
  } catch {
    return null;
  }
}

/**
 * Set the stream lock in localStorage.
 */
export function setStreamLock(lock: StreamLock): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STREAM_LOCK_KEY, JSON.stringify(lock));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Remove the stream lock from localStorage.
 */
export function removeStreamLock(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STREAM_LOCK_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Get connected workspace info from localStorage.
 */
export function getConnectedWorkspaceInfo(): ConnectedWorkspaceInfo | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONNECTED_WORKSPACE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as ConnectedWorkspaceInfo;
  } catch {
    return null;
  }
}

/**
 * Set connected workspace info in localStorage.
 */
export function setConnectedWorkspaceInfo(info: ConnectedWorkspaceInfo): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(CONNECTED_WORKSPACE_KEY, JSON.stringify(info));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Remove connected workspace info from localStorage.
 */
export function removeConnectedWorkspaceInfo(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONNECTED_WORKSPACE_KEY);
  } catch {
    // Ignore errors
  }
}
